import { Dropdown } from 'primereact/dropdown'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button, Flex, Text } from '../../../../Components'
import { cap } from '../../../../State'
import { translations } from '../../../../translations'
import {
  AddServiceButton,
  ServiceEmptyState,
  SingleServiceContainer,
  TransferField
} from '../Components'
import {
  addService,
  getElementsByService,
  getServiceCount,
  readKey,
  removeService,
  writeKey
} from '../Functions'
import { SectionShell } from '../SectionShell'
import { useGigiFormValidation } from '../ValidationContext'
import { getDiffStatus } from './Functions'
import { buildHotelServiceSchema } from './Hotel.schema'
import { useMultiServiceZodValidation } from './multiServiceValidation'
const language = cap.language.state || 'IT'
const t = translations[language]

const {
  hotel: {
    checkInDate,
    checkOutDate,
    hotelCity,
    hotelName,
    hotelStars,
    hotelParking,
    treatment,
    noteAggiuntive = ''
  }
} = getElementsByService()

const SECTION_ID = 'hotel'

const ROOM_OPTIONS_LOCATION =
  'transferInfo.infoPax.listOfPax.0.infoServices.hotel.#COUNT.hotelRoomOptions'
const ROOM_TYPES = [
  { label: t.ggRoomSingola, value: 1 },
  { label: t.ggRoomDoppia, value: 2 },
  { label: t.ggRoomTripla, value: 3 }
]

const HotelRoomOptionsEditor = ({ state: [json, setJson], hotelIndex, readOnly }) => {
  const stateOptions = readKey(json, ROOM_OPTIONS_LOCATION, hotelIndex) || []
  const [options, setOptions] = useState(stateOptions)

  useEffect(() => {
    setOptions(readKey(json, ROOM_OPTIONS_LOCATION, hotelIndex) || [])
  }, [json?.transferInfo?.infoPax?.listOfPax?.[0]?.infoServices?.hotel])

  const updateOptions = (newOptions) => {
    setOptions(newOptions)
    const updated = writeKey(
      JSON.parse(JSON.stringify(json)),
      ROOM_OPTIONS_LOCATION,
      hotelIndex,
      newOptions
    )
    if (Object.keys(updated).length) setJson(updated)
  }

  const addOption = () => {
    if (options.length >= 2) return
    updateOptions([...options, { roomsComposition: [{ adults: 2 }] }])
  }

  const removeOption = (optIdx) => {
    updateOptions(options.filter((_, i) => i !== optIdx))
  }

  const addRoom = (optIdx) => {
    const next = options.map((opt, i) =>
      i === optIdx ? { ...opt, roomsComposition: [...opt.roomsComposition, { adults: 2 }] } : opt
    )
    updateOptions(next)
  }

  const removeRoom = (optIdx, roomIdx) => {
    const next = options.map((opt, i) =>
      i === optIdx
        ? { ...opt, roomsComposition: opt.roomsComposition.filter((_, ri) => ri !== roomIdx) }
        : opt
    )
    updateOptions(next)
  }

  const changeRoomAdults = (optIdx, roomIdx, adults) => {
    const next = options.map((opt, i) =>
      i === optIdx
        ? {
            ...opt,
            roomsComposition: opt.roomsComposition.map((r, ri) => (ri === roomIdx ? { adults } : r))
          }
        : opt
    )
    updateOptions(next)
  }

  return (
    <Flex fw style={{ marginTop: 10 }}>
      <Flex fw row jb style={{ marginBottom: 6 }}>
        <Text value={t.ggComposizioneCamere} bold size={13} />
        {!readOnly && options.length < 2 && (
          <Button
            icon="plus"
            label={t.ggAggiungiOpzione}
            onClick={addOption}
            style={{ padding: '4px 10px', fontSize: 12 }}
          />
        )}
      </Flex>
      {!options.length && (
        <Text
          value={t.ggNessunaPreferenzaCamere}
          size={12}
          style={{ color: 'var(--ink-400)', marginBottom: 4 }}
        />
      )}
      {options.map((opt, optIdx) => (
        <Flex
          key={optIdx}
          fw
          style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 10,
            marginBottom: 8
          }}
        >
          <Flex fw row jb style={{ marginBottom: 6 }}>
            <Text value={`${t.ggOpzione} ${optIdx + 1}`} bold size={12} />
            <Flex row>
              {!readOnly && (
                <Button
                  icon="plus"
                  label={t.ggCamera}
                  onClick={() => addRoom(optIdx)}
                  style={{ padding: '2px 8px', fontSize: 11, marginRight: 6 }}
                />
              )}
              {!readOnly && (
                <Button
                  icon="close"
                  round
                  onClick={() => removeOption(optIdx)}
                  style={{ width: 24, height: 24 }}
                />
              )}
            </Flex>
          </Flex>
          {(opt.roomsComposition || []).map((room, roomIdx) => (
            <Flex key={roomIdx} fw row as style={{ marginBottom: 4, gap: 8, alignItems: 'center' }}>
              <Text value={`${t.ggCamera} ${roomIdx + 1}:`} size={12} style={{ minWidth: 80 }} />
              <Dropdown
                value={room.adults}
                options={ROOM_TYPES}
                onChange={(e) => changeRoomAdults(optIdx, roomIdx, e.value)}
                disabled={readOnly}
                style={{ width: 200, fontSize: 12 }}
                placeholder={t.ggTipoCamera}
              />
              {!readOnly && (opt.roomsComposition || []).length > 1 && (
                <Button
                  icon="close"
                  round
                  onClick={() => removeRoom(optIdx, roomIdx)}
                  style={{ width: 22, height: 22 }}
                />
              )}
            </Flex>
          ))}
        </Flex>
      ))}
    </Flex>
  )
}

export const HotelSection = ({ state: [state, setState], readOnly, diff, enableMultiPax }) => {
  const [valTick, setValTick] = useState(0)
  const trackingSetState = (data, forceRender) => {
    setState(data, forceRender)
    setValTick((v) => v + 1)
  }
  const stateProp = [state, trackingSetState]
  const statePropRender = [state, (data) => trackingSetState(data, true)]
  const containerRef = useRef(null)
  const { sectionHasErrors } = useGigiFormValidation()

  const hotelCount = getServiceCount(state, 'hotel')

  const buildSchema = useCallback(() => buildHotelServiceSchema(), [])
  const buildServiceValues = useCallback(
    (formState, i) => ({
      checkInDate: readKey(formState, checkInDate.location, i) ?? '',
      checkOutDate: readKey(formState, checkOutDate.location, i) ?? '',
      hotelCity: readKey(formState, hotelCity.location, i) ?? '',
      hotelName: readKey(formState, hotelName.location, i) ?? '',
      hotelStars: readKey(formState, hotelStars.location, i) ?? '',
      hotelParking: readKey(formState, hotelParking.location, i) ?? '',
      treatment: readKey(formState, treatment.location, i) ?? '',
      noteAggiuntive: readKey(formState, noteAggiuntive.location, i) ?? ''
    }),
    []
  )

  useMultiServiceZodValidation({
    sectionId: SECTION_ID,
    containerRef,
    state,
    serviceCount: hotelCount,
    buildSchema,
    buildServiceValues,
    trigger: valTick
  })

  return (
    <SectionShell
      id="hotel"
      icon="hotel"
      title={t.serviziHotel?.toUpperCase() || 'HOTEL'}
      storageKey="gigiForm.section.hotel"
      hasErrors={sectionHasErrors('hotel')}
      count={hotelCount}
      contentId="hotel-content"
      headerExtra={
        !readOnly && (
          <AddServiceButton
            label={t.aggiungiServizio}
            onClick={async () => setState(await addService(state, 'hotel'))}
          />
        )
      }
    >
      <div ref={containerRef}>
        <Flex fw>
          {!hotelCount && (
            <ServiceEmptyState
              icon="hotel"
              title={t.nessunServizioHotelInserito}
              hint={t.servizioEmptyHint}
            />
          )}
          {Array(getServiceCount(state, 'hotel'))
            .fill()
            .map((_, i) => {
              const departureDateValue = readKey(state, checkInDate?.location, i)
              const minDateCheckOut = departureDateValue
                ? new Date(
                    new Date(
                      departureDateValue.split('/')[2],
                      departureDateValue.split('/')[1] - 1,
                      departureDateValue.split('/')[0]
                    )
                  )
                : new Date()
              return (
                <SingleServiceContainer
                  key={i}
                  index={i}
                  serviceType="hotel"
                  state={state}
                  onRemove={() => setState(removeService(state, 'hotel', i))}
                  readOnly={readOnly}
                >
                  <TransferField
                    state={statePropRender}
                    item={checkInDate}
                    key="checkInDate"
                    count={i}
                    readOnly={readOnly}
                    diffStatus={getDiffStatus(checkInDate, diff, i)}
                  />
                  <TransferField
                    state={stateProp}
                    item={{ ...checkOutDate, minDate: minDateCheckOut }}
                    key="checkOutDate"
                    count={i}
                    readOnly={readOnly}
                    diffStatus={getDiffStatus(checkOutDate, diff, i)}
                  />
                  <TransferField
                    state={stateProp}
                    item={hotelCity}
                    key="hotelCity"
                    count={i}
                    readOnly={readOnly}
                    diffStatus={getDiffStatus(hotelCity, diff, i)}
                  />
                  <TransferField
                    state={stateProp}
                    item={hotelName}
                    key="hotelName"
                    count={i}
                    readOnly={readOnly}
                    diffStatus={getDiffStatus(hotelName, diff, i)}
                  />
                  <TransferField
                    state={stateProp}
                    item={hotelStars}
                    key="hotelStars"
                    count={i}
                    readOnly={readOnly}
                    diffStatus={getDiffStatus(hotelStars, diff, i)}
                  />
                  <TransferField
                    state={stateProp}
                    item={hotelParking}
                    key="hotelParking"
                    count={i}
                    readOnly={readOnly}
                    diffStatus={getDiffStatus(hotelParking, diff, i)}
                  />
                  <TransferField
                    state={stateProp}
                    item={treatment}
                    key="treatment"
                    count={i}
                    readOnly={readOnly}
                    diffStatus={getDiffStatus(treatment, diff, i)}
                  />
                  <div aria-hidden className="ff-service-grid--break" />
                  <div className="ff-service-grid--full">
                    <TransferField
                      state={stateProp}
                      item={noteAggiuntive}
                      key="noteAggiuntive"
                      count={i}
                      readOnly={readOnly}
                      diffStatus={getDiffStatus(noteAggiuntive, diff, i)}
                    />
                  </div>
                  {!!(enableMultiPax && (state?.transferInfo?.infoPax?.numPax || 1) > 1) && (
                    <div className="ff-service-grid--full">
                      <HotelRoomOptionsEditor
                        state={stateProp}
                        hotelIndex={i}
                        readOnly={readOnly}
                      />
                    </div>
                  )}
                </SingleServiceContainer>
              )
            })}
        </Flex>
      </div>
    </SectionShell>
  )
}
